import {
  APP_BASE_HREF,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import {
  FREELANCER_HTTP_CONFIG,
  FreelancerHttpConfig,
} from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { Pwa } from '@freelancer/pwa';
import { ErrorTracking, PerformanceTracking } from '@freelancer/tracking';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RESPONSE } from '@nguniversal/express-engine/tokens';
import { Response } from 'express';
import { firstValueFrom } from 'rxjs';

export interface RedirectToPhpOptions {
  // If set to false, will not replace current url when redirect to php.
  isInPageGuard: boolean;
}

@UntilDestroy({ className: 'RedirectToPhp' })
@Injectable({
  providedIn: 'root',
})
export class RedirectToPhp {
  // We don't want to push states to the history if no route has been loaded,
  // i.e. if the webapp performed a redirect on start
  constructor(
    private pwa: Pwa,
    private auth: Auth,
    private performanceTracking: PerformanceTracking,
    @Inject(FREELANCER_HTTP_CONFIG)
    private freelancerHttpConfig: FreelancerHttpConfig,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(APP_BASE_HREF) private baseHref: string,
    @Optional() @Inject(RESPONSE) private response: Response,
    private errorTracking: ErrorTracking,
  ) {}

  doRedirect(url: string, options?: RedirectToPhpOptions): Promise<boolean> {
    if (url.includes('w=f') && url.includes('referrer=webapp')) {
      this.errorTracking.captureMessage(
        'Webapp to PHP redirect loop. The redirect has happened at least 2 times.',
        { url },
      );
    }

    // Add ?w=f (webapp false) search param to the URL in order to force the
    // PHP version on routes serving both webapp & php pages
    // Also add referrer=webapp to track potential redirect loops
    let redirectUrl: string;
    const u = this.baseHref
      ? new URL(
          `${this.location.origin}${this.baseHref.replace(/\/$/, '')}${url}`,
        )
      : new URL(`${this.location.origin}${url}`);
    const s = u.searchParams;
    s.set('w', 'f');
    s.set('referrer', 'webapp');
    u.search = s.toString();
    redirectUrl = u.toString();
    if (isPlatformBrowser(this.platformId)) {
      // Log the redirect time
      console.log(`redirected in ${Math.round(window.performance.now())} ms`);
      this.performanceTracking.mark('webapp_php_redirect', true);
    }
    // When in localhost, redirect to the freelancerHttpConfig.baseUrl
    // and transfer auth state using the internal auth/callback route.
    const isLocalhost =
      this.location.hostname === 'localhost' ||
      this.location.hostname.startsWith('192.168.') ||
      this.location.hostname.startsWith('10.');

    // Make legacy pages redirect internally so that there are no unnecessary 302s
    if (isPlatformServer(this.platformId)) {
      this.response.set('X-Varnish-Follow-Redirect', 'true');
    }

    const replaceUrl = options?.isInPageGuard ?? true;
    if (
      isLocalhost &&
      // This doesn't need to be done in native apps because
      // redirect() does it already when opening the in-app browser
      !this.pwa.isNative()
    ) {
      return firstValueFrom(
        this.auth.authState$.pipe(untilDestroyed(this)),
      ).then(state => {
        redirectUrl = `${u.pathname}${u.search}${u.hash}`;
        if (state) {
          return this.location.redirect(
            `${this.freelancerHttpConfig.baseUrl}/internal/auth/callback?uid=${
              state.userId
            }&token=${encodeURIComponent(
              state.token,
            )}&next=${encodeURIComponent(redirectUrl)}`,
            replaceUrl,
          );
        }
        return this.location.redirect(
          `${this.freelancerHttpConfig.baseUrl}${redirectUrl}`,
          replaceUrl,
        );
      });
    }
    return this.location.redirect(redirectUrl, replaceUrl);
  }
}
